import React from "react";
import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

type SetupStatus = 'unconfigured' | 'in_progress' | 'completed' | null;

interface AdAccount {
  id: string;
  name: string;
  account_status: number;
  currency: string;
}

interface AdAccountContextType {
  selectedAdAccountId: string | null;
  selectedAdAccountName: string | null;
  setSelectedAdAccount: (id: string | null, name: string | null) => void;
  adAccounts: AdAccount[];
  isLoading: boolean;
  activeAccountSetupStatus: SetupStatus;
  isAccountConfigured: boolean;
  refetchAdAccounts: () => void;
}

const AdAccountContext = createContext<AdAccountContextType | undefined>(undefined);

const STORAGE_KEY = 'tbd-selected-ad-account';

export function AdAccountProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedAdAccountId, setSelectedAdAccountId] = useState<string | null>(null);
  const [selectedAdAccountName, setSelectedAdAccountName] = useState<string | null>(null);

  // Fetch ad accounts from Supabase via React Query
  const { data: adAccountsData, isLoading } = useQuery({
    queryKey: ['ad-account-list', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ad_accounts')
        .select('ad_account_id, ad_account_name, currency, status')
        .eq('user_id', user!.id)
        .eq('status', 'ACTIVE');

      if (error) throw error;

      return (data ?? []).map(row => ({
        id: row.ad_account_id,
        name: row.ad_account_name || '',
        account_status: 1,
        currency: row.currency || '',
      })) as AdAccount[];
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000,
  });

  const adAccounts = useMemo(() => adAccountsData ?? [], [adAccountsData]);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const { id, name } = JSON.parse(stored);
        setSelectedAdAccountId(id);
        setSelectedAdAccountName(name);
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, []);

  // Auto-select first account if none selected and accounts are loaded
  useEffect(() => {
    if (!selectedAdAccountId && adAccounts.length > 0) {
      const first = adAccounts[0];
      setSelectedAdAccount(first.id, first.name);
    }
  }, [adAccounts, selectedAdAccountId]);

  // Fetch setup_status from ad_accounts table via TanStack Query
  const { data: adAccountRow } = useQuery({
    queryKey: ['ad-account-row', user?.id, selectedAdAccountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ad_accounts')
        .select('setup_status')
        .eq('user_id', user!.id)
        .eq('ad_account_id', selectedAdAccountId!)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && !!selectedAdAccountId,
    staleTime: 0,
  });

  const activeAccountSetupStatus: SetupStatus = (adAccountRow?.setup_status as SetupStatus) ?? null;
  const isAccountConfigured = activeAccountSetupStatus === 'completed';

  const refetchAdAccounts = () => {
    queryClient.invalidateQueries({ queryKey: ['ad-account-list', user?.id], refetchType: 'active' });
    queryClient.invalidateQueries({ queryKey: ['ad-account-statuses', user?.id], refetchType: 'active' });
    queryClient.invalidateQueries({ queryKey: ['ad-account-row'], refetchType: 'active' });
  };

  const setSelectedAdAccount = (id: string | null, name: string | null) => {
    setSelectedAdAccountId(id);
    setSelectedAdAccountName(name);
    
    if (id && name) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ id, name }));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  return (
    <AdAccountContext.Provider
      value={{
        selectedAdAccountId,
        selectedAdAccountName,
        setSelectedAdAccount,
        adAccounts,
        isLoading,
        activeAccountSetupStatus,
        isAccountConfigured,
        refetchAdAccounts,
      }}
    >
      {children}
    </AdAccountContext.Provider>
  );
}

export function useAdAccount() {
  const context = useContext(AdAccountContext);
  if (!context) {
    throw new Error('useAdAccount must be used within an AdAccountProvider');
  }
  return context;
}
